import { assignInlineVars } from '@vanilla-extract/dynamic'
import * as styles from './repo-risk-donut.css.ts'
import { bandForScore } from './repository-risk.ts'

/**
 * Ring geometry, in the SVG's own user units — independent of the rendered size, which the viewBox scales.
 *
 * `pathLength` renormalises the circumference to 100 whatever `2πr` actually works out to, so the dash
 * array is literally the score and there is no arithmetic to keep in step if the radius ever changes.
 */
const RADIUS = 16
const STROKE_WIDTH = 4
const VIEWBOX_SIZE = 40
const PATH_LENGTH = 100

export type RepoRiskDonutProps = {
  /** 0–100, where higher is riskier. Rendered rounded. Only the arc clamps — see `filled`. */
  riskScore: number
  /** Any CSS length. Everything inside scales with it, so 40px and 64px both read correctly. */
  size?: string
}

/**
 * A risk score as a ring filled in proportion to it, coloured by the band the score falls in.
 *
 * Replaced the letter-grade badge: a grade compresses the score into five buckets, and the ranking this
 * view exists to show needs the difference between a 62 and a 79 to be visible. The band survives as the
 * arc's colour, so the coarse read is still available at a glance.
 */
/** Imported by the dashed placeholder that stands in for an unscored ring, so the two diameters
 * cannot drift apart — nothing else would catch it. */
export const REPO_RISK_DONUT_DEFAULT_SIZE = '40px'

export const RepoRiskDonut: React.FC<RepoRiskDonutProps> = ({ riskScore, size = REPO_RISK_DONUT_DEFAULT_SIZE }) => {
  // Only the geometry clamps. The number and the aria-label below keep the raw score on purpose: a ring
  // cannot draw past full, but a 140 that renders as "100" would hide the bad data that produced it.
  const filled = Math.max(0, Math.min(PATH_LENGTH, riskScore))
  const centre = VIEWBOX_SIZE / 2
  return (
    // One labelled image rather than a bare number: read on its own, "91" says nothing about what scale
    // it sits on, and the ring that would answer that is not something a screen reader can see.
    <span
      role="img"
      aria-label={`Risk score ${Math.round(riskScore)} of 100`}
      className={styles.donut}
      style={assignInlineVars({
        [styles.arcColor]: styles.RISK_BAND_ARC_COLORS[bandForScore(riskScore).id],
        [styles.size]: size,
      })}
    >
      <svg className={styles.svg} viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`} aria-hidden>
        <circle className={styles.track} cx={centre} cy={centre} r={RADIUS} strokeWidth={STROKE_WIDTH} />
        <circle
          className={styles.arc}
          cx={centre}
          cy={centre}
          r={RADIUS}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          pathLength={PATH_LENGTH}
          strokeDasharray={`${filled} ${PATH_LENGTH - filled}`}
        />
      </svg>
      <span className={styles.value}>{Math.round(riskScore)}</span>
    </span>
  )
}
